import { apiClient } from './client';
import {
  LoginResponse,
  RefreshResponse,
  TwoFactorRequiredResponse,
  ForgotPasswordResponse,
  LoginCredentials,
  ForgotPasswordRequest,
  ValidateResetTokenRequest,
  ValidateResetTokenResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from '../types';

/**
 * Perform login via Apigee gateway.
 *
 * Apigee calls POST /accounts/apigee-login/ on Django and returns:
 *   200 — success: {token, refresh, expires} + sessionid cookie
 *   202 — 2FA required: {two_factor_required: true, last_four_digits}
 *
 * Pass two_factor_code to verify a previously-sent SMS code.
 * Omit two_factor_code (or call without it) to trigger a new SMS send.
 */
export const login = async (
  credentials: LoginCredentials & { two_factor_code?: string }
): Promise<
  | { status: 200; data: LoginResponse }
  | { status: 202; data: TwoFactorRequiredResponse }
> => {
  const body: Record<string, string> = {
    email: credentials.email,
    password: credentials.password,
  };
  if (credentials.two_factor_code) {
    body.two_factor_code = credentials.two_factor_code;
  }
  const response = await apiClient.post('/v1/auth/login', body);
  return { status: response.status as 200 | 202, data: response.data };
};

/**
 * Refresh an expired access token.
 *
 * Apigee invalidates the refresh token it was sent and returns a replacement in
 * `refresh` — callers must persist it, or the next refresh is a replay.
 */
export const refreshToken = async (refresh: string): Promise<RefreshResponse> => {
  const response = await apiClient.post<RefreshResponse>('/v1/auth/refresh', {
    refresh,
  });
  return response.data;
};

/**
 * Send password reset email via Apigee (wraps mid-tier GraphQL)
 */
export const sendPasswordResetEmail = async (
  request: ForgotPasswordRequest
): Promise<ForgotPasswordResponse> => {
  const response = await apiClient.post<ForgotPasswordResponse>(
    '/v1/auth/forgot-password',
    { email: request.email }
  );
  return response.data;
};

/**
 * Validate a Django password reset token via Apigee.
 * Returns a session_token needed for the actual reset call.
 */
export const validateResetToken = async (
  request: ValidateResetTokenRequest
): Promise<ValidateResetTokenResponse> => {
  const response = await apiClient.post<ValidateResetTokenResponse>(
    '/v1/auth/validate-reset-token',
    request
  );
  return response.data;
};

/**
 * Validate that both the access token and the Django session cookie are still active.
 * Apigee verifies the JWT; the browser sends the sessionid cookie via withCredentials.
 * Returns true if both are valid, false otherwise.
 * Only relevant while SKIP_LEGACY_SESSION is false (dual-mint mode).
 */
export const validateSession = async (token: string): Promise<boolean> => {
  try {
    await apiClient.get('/v1/auth/validate', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * Invalidate the refresh token in Apigee's cache and notify Django.
 * Fire-and-forget — always resolves (never throws).
 */
export const logout = async (refreshToken: string, accessToken: string): Promise<void> => {
  try {
    await apiClient.post(
      '/v1/auth/logout',
      { refresh: refreshToken },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
  } catch {
    // Ignore — local storage is cleared regardless
  }
};

/**
 * Reset password using a validated session_token from validateResetToken.
 */
export const resetPassword = async (
  request: ResetPasswordRequest
): Promise<ResetPasswordResponse> => {
  const response = await apiClient.post<ResetPasswordResponse>(
    '/v1/auth/reset-password',
    request
  );
  return response.data;
};

/**
 * Exchange a Vida access token for a Zendesk Web SSO JWT, signed with
 * ZENDESK_JWT_SECRET_FOR_VIDA_SSO. The returned JWT is intended to be sent
 * to {ZENDESK_URL}/access/jwt to establish a Zendesk session for the user.
 *
 * Auth is the Bearer header end-to-end: Apigee's VerifyJWT-Access validates
 * it, then the webserver's request.user is populated from the same header.
 * No body is required.
 */
export const obtainZendeskSSOToken = async (userToken: string): Promise<string> => {
  const response = await apiClient.post<{ jwt: string }>(
    '/api/v1/zendesk-sso-token/',
    undefined,
    { headers: { Authorization: `Bearer ${userToken}` } }
  );
  return response.data.jwt;
};
